import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { dateFr, DEVIS_STATUS, FACTURE_STATUS } from "@/lib/constants";
import { DEFAULT_LOGO_BASE64 } from "@/lib/logo";

const BLUE = [2, 132, 199];
const GREEN = [22, 163, 74];
const DARK = [15, 23, 42];
const GREY = [148, 163, 184];

// PDF-safe euro formatter: jsPDF standard fonts can't render the narrow
// no-break space (U+202F). Force plain ASCII spaces and a normal space before €.
function eurPdf(n) {
  const v = Number(n);
  const safe = Number.isFinite(v) ? v : 0;
  const s = safe.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return s.replace(/[\u202f\u00a0\u2007\s]/g, " ") + " \u20AC";
}

const tvaCell = (rate) => (Number(rate) === 0 ? "Non appl." : `${Number(rate)}%`);

// Two sober side-by-side boxes: client signature + MS ÉNERGIE validation.
function drawSignatureBoxes(pdf, x, y, W, M, validationDate) {
  const gap = 8;
  const boxW = (W - 2 * M - gap) / 2;
  const boxH = 34;
  const r = 2;

  // Left box — Signature du client
  pdf.setDrawColor(...GREY);
  pdf.setLineWidth(0.3);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, boxW, boxH, r, r, "FD");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setTextColor(...DARK);
  pdf.text("Signature du client", x + 4, y + 7);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(...GREY);
  pdf.text("Précédée de la mention « Bon pour accord »", x + 4, y + 12);

  // Right box — Validation MS ÉNERGIE
  const rx = x + boxW + gap;
  pdf.setDrawColor(...GREY);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(rx, y, boxW, boxH, r, r, "FD");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9.5);
  pdf.setTextColor(...DARK);
  pdf.text("Validation MS ÉNERGIE", rx + 4, y + 7);
  // small discreet green check
  const cx = rx + 5.5;
  const cy = y + 15.5;
  pdf.setDrawColor(...GREEN);
  pdf.setLineWidth(0.9);
  pdf.line(cx - 1.6, cy, cx - 0.4, cy + 1.4);
  pdf.line(cx - 0.4, cy + 1.4, cx + 2.2, cy - 1.8);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8.5);
  pdf.setTextColor(...GREEN);
  pdf.text("Validé par MS ÉNERGIE", rx + 9, y + 16.5);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...DARK);
  pdf.text(`Le ${dateFr(validationDate)}`, rx + 4, y + 24);

  return boxH;
}

// doc: devis/facture/avoir object (with .client), settings: company info,
// kind: "devis" | "facture" | "avoir"
export function generatePdf(doc, settings, kind) {
  const isAvoir = doc.doc_type === "avoir" || kind === "avoir";
  const realKind = isAvoir ? "avoir" : kind;
  const isDraft = kind === "facture" && !doc.number;

  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 14;

  const logoData = settings.logo_base64 || DEFAULT_LOGO_BASE64;
  const logoFmt = typeof logoData === "string" && logoData.startsWith("data:image/jpeg") ? "JPEG" : "PNG";
  let logoH = 16;
  if (logoData) {
    try {
      const props = pdf.getImageProperties(logoData);
      if (props?.width && props?.height) logoH = (50 * props.height) / props.width;
    } catch (e) { /* keep default */ }
  }

  const title = realKind === "devis" ? "DEVIS" : realKind === "avoir" ? "AVOIR" : "FACTURE";
  const statusMap = realKind === "devis" ? DEVIS_STATUS : FACTURE_STATUS;
  const dateLabel = realKind === "devis" ? doc.date : doc.date_emission;
  const HEADER_BOTTOM = Math.max(30, 8 + logoH + 18);
  const FOOTER_SEP = H - 30;         // footer separator line
  const CONTENT_BOTTOM = FOOTER_SEP - 4;
  const hfPages = {};

  // ---- Header + footer, repeated on every page ----
  const drawHeaderFooter = () => {
    pdf.setFillColor(...BLUE);
    pdf.rect(0, 0, W, 4, "F");

    if (logoData) {
      try { pdf.addImage(logoData, logoFmt, M, 8, 50, logoH); } catch (e) { /* ignore */ }
    } else {
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(20); pdf.setTextColor(...BLUE);
      pdf.text(settings.name || "MS ÉNERGIE", M, 20);
    }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...DARK);
    let cy = 8 + logoH + 4.5;
    [
      settings.address,
      settings.phone ? `Tél : ${settings.phone}` : "",
      settings.email,
      settings.siret ? `SIRET : ${settings.siret}` : "",
    ].filter(Boolean).forEach((l) => { pdf.text(l, M, cy); cy += 4; });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(...(realKind === "avoir" ? [220, 38, 38] : GREEN));
    pdf.text(title, W - M, 16, { align: "right" });
    pdf.setFontSize(11);
    pdf.setTextColor(...DARK);
    pdf.text(doc.number || (isDraft ? "BROUILLON" : ""), W - M, 23, { align: "right" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    let my = 29;
    if (dateLabel) { pdf.text(`Date : ${dateFr(dateLabel)}`, W - M, my, { align: "right" }); my += 5; }
    if (realKind === "facture" && doc.date_echeance) { pdf.text(`Échéance : ${dateFr(doc.date_echeance)}`, W - M, my, { align: "right" }); my += 5; }
    if (isAvoir && doc.facture_origine_number) { pdf.text(`Facture : ${doc.facture_origine_number}`, W - M, my, { align: "right" }); my += 5; }
    const stLabel = statusMap[doc.status]?.label || (isDraft ? "Brouillon" : doc.status);
    if (stLabel) pdf.text(`Statut : ${stLabel}`, W - M, my, { align: "right" });

    // ---- Footer ----
    let fy = FOOTER_SEP;
    pdf.setDrawColor(...BLUE);
    pdf.setLineWidth(0.3);
    pdf.line(M, fy, W - M, fy);
    fy += 4.5;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...BLUE);
    pdf.text("Conditions de paiement", M, fy);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...DARK);
    fy += 3.6;
    pdf.text(pdf.splitTextToSize(settings.payment_terms || "", W - 2 * M), M, fy);
    fy += 6.5;
    pdf.text(pdf.splitTextToSize(settings.warranty || "", W - 2 * M), M, fy);
    fy += 6.5;
    if (settings.iban) { pdf.setFont("helvetica", "bold"); pdf.text(`IBAN : ${settings.iban}`, M, fy); }

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...GREY);
    const pageNo = pdf.internal.getNumberOfPages();
    pdf.text(`Page ${pageNo}`, W - M, H - 6, { align: "right" });
  };

  // ---- Page-1 blocks before the table (client + equipment) ----
  drawHeaderFooter();
  hfPages[1] = true;
  let y = HEADER_BOTTOM + 2;
  pdf.setDrawColor(...BLUE);
  pdf.setLineWidth(0.4);
  pdf.line(M, y, W - M, y);
  y += 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...BLUE);
  pdf.text("CLIENT", M, y);
  pdf.setTextColor(...DARK);
  y += 5;
  const c = doc.client || {};
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.text(c.name || "-", M, y);
  pdf.setFont("helvetica", "normal");
  y += 5;
  const chantier = doc.chantier_address || c.address || "";
  if (chantier) { pdf.text(`Chantier : ${chantier}`, M, y); y += 5; }
  if (c.phone) { pdf.text(`Tél : ${c.phone}`, M, y); y += 5; }
  if (c.siret) { pdf.text(`SIRET : ${c.siret}`, M, y); y += 5; }
  const chantierTypeLabel = doc.chantier_type === "Autre" && doc.chantier_type_other ? doc.chantier_type_other : doc.chantier_type;
  if (chantierTypeLabel) { pdf.setFont("helvetica", "bold"); pdf.text(`Type de chantier : ${chantierTypeLabel}`, M, y); y += 5; }

  const eq = doc.equipment || {};
  const hasEq = eq.material_type || eq.brand_model || eq.power || (eq.accessoires && eq.accessoires.length);
  if (hasEq) {
    y += 2;
    const eqLines = [];
    if (eq.material_type) eqLines.push(`Type : ${eq.material_type}`);
    if (eq.brand_model) eqLines.push(`Marque / Modèle : ${eq.brand_model}`);
    if (eq.power) eqLines.push(`Puissance : ${eq.power}`);
    if (eq.accessoires && eq.accessoires.length) eqLines.push(`Accessoires : ${eq.accessoires.join(", ")}`);
    const boxH = 7 + eqLines.length * 4.6;
    pdf.setFillColor(240, 249, 245);
    pdf.rect(M, y, W - 2 * M, boxH, "F");
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(...GREEN);
    pdf.text("MATÉRIEL INSTALLÉ", M + 3, y + 5);
    pdf.setFont("helvetica", "normal"); pdf.setTextColor(...DARK);
    let ey = y + 10;
    eqLines.forEach((l) => { pdf.text(l, M + 3, ey); ey += 4.6; });
    y += boxH;
  }

  // ---- Lines table (auto-paginates, repeats head, never splits a row) ----
  const body = (doc.lines || []).map((l) => {
    const ht = Number(l.quantity) * Number(l.unit_price_ht);
    return [l.designation || "", l.prestation_type || "", String(l.quantity), eurPdf(l.unit_price_ht), tvaCell(l.tva_rate), eurPdf(ht)];
  });

  autoTable(pdf, {
    startY: y + 4,
    head: [["Désignation", "Type", "Qté", "P.U. HT", "TVA", "Total HT"]],
    body,
    theme: "grid",
    rowPageBreak: "avoid",
    headStyles: { fillColor: BLUE, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: DARK },
    columnStyles: {
      0: { cellWidth: 66 },
      1: { cellWidth: 24 },
      2: { halign: "center", cellWidth: 12 },
      3: { halign: "right", cellWidth: 24 },
      4: { halign: "center", cellWidth: 18 },
      5: { halign: "right", cellWidth: 26 },
    },
    margin: { left: M, right: M, top: HEADER_BOTTOM + 2, bottom: H - FOOTER_SEP + 6 },
    didDrawPage: () => {
      const p = pdf.internal.getNumberOfPages();
      if (!hfPages[p]) { hfPages[p] = true; drawHeaderFooter(); }
    },
  });

  // ---- Totals + signature (kept whole; lifted right under the table) ----
  let ty = pdf.lastAutoTable.finalY + 8;
  const noTva = Number(doc.total_tva) === 0;
  const hasDiscount = Math.abs(Number(doc.discount_amount)) > 0;

  let totalsH = 6 + 6 + 3 + 8;
  if (hasDiscount) totalsH += 12;
  if (noTva && settings.tva_exemption_mention) totalsH += 10;
  const sigH = isAvoir ? 0 : 34;
  const blockH = totalsH + 12 + sigH;

  if (ty + blockH > CONTENT_BOTTOM) {
    pdf.addPage();
    const p = pdf.internal.getNumberOfPages();
    hfPages[p] = true;
    drawHeaderFooter();
    ty = HEADER_BOTTOM + 6;
  }

  const boxX = W - M - 70;
  pdf.setFontSize(10);
  pdf.setTextColor(...DARK);
  pdf.setFont("helvetica", "normal");
  if (hasDiscount) {
    pdf.text("Sous-total HT", boxX, ty);
    pdf.text(eurPdf(doc.subtotal_ht), W - M, ty, { align: "right" });
    ty += 6;
    const dlabel = doc.discount_type === "percent" ? `Remise (${Number(doc.discount_value)}%)` : "Remise";
    pdf.setTextColor(...GREEN);
    pdf.text(dlabel, boxX, ty);
    pdf.text("- " + eurPdf(Math.abs(doc.discount_amount)), W - M, ty, { align: "right" });
    pdf.setTextColor(...DARK);
    ty += 6;
  }
  pdf.text("Total HT", boxX, ty);
  pdf.text(eurPdf(doc.total_ht), W - M, ty, { align: "right" });
  ty += 6;
  if (noTva) {
    pdf.setFont("helvetica", "italic");
    pdf.setTextColor(...GREY);
    pdf.text("TVA non applicable", boxX, ty);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...DARK);
    pdf.text(eurPdf(0), W - M, ty, { align: "right" });
  } else {
    pdf.text("TVA", boxX, ty);
    pdf.text(eurPdf(doc.total_tva), W - M, ty, { align: "right" });
  }
  ty += 3;
  pdf.setDrawColor(...GREEN);
  pdf.setLineWidth(0.5);
  pdf.line(boxX, ty, W - M, ty);
  ty += 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(...(realKind === "avoir" ? [220, 38, 38] : GREEN));
  pdf.text(realKind === "avoir" ? "TOTAL AVOIR" : "TOTAL TTC", boxX, ty);
  pdf.text(eurPdf(doc.total_ttc), W - M, ty, { align: "right" });

  if (noTva && settings.tva_exemption_mention) {
    ty += 7;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...DARK);
    pdf.text(pdf.splitTextToSize(settings.tva_exemption_mention, 90), boxX, ty);
  }

  if (!isAvoir) {
    const sy = ty + 12;
    const validationDate = doc.date_emission || doc.date || new Date().toISOString().slice(0, 10);
    drawSignatureBoxes(pdf, M, sy, W, M, validationDate);
  }

  const fileName = (doc.number || (isDraft ? `${title}-brouillon` : title)) + ".pdf";
  pdf.save(fileName);
}
